import { useMemo, useState } from 'react';

/**
 * ChannelMap - Visual flow showing sell-in vs sell-out by channel
 *
 * Phase 4: Channel Section (Sell-In/Sell-Out Map)
 * Goal: Visual map showing channel flow with sell-in vs sell-out distinction
 *
 * Sell-In: What company sells TO channels (manufacturer → retailer)
 * Sell-Out: What channels sell TO consumers (retailer → end consumer)
 *
 * Gap = Sell-In - Sell-Out
 * - Positive gap: Inventory building (channel stocking up)
 * - Negative gap: Inventory depleting (consumer demand > supply)
 */

export interface ChannelFlowData {
  id: string;
  name: string;
  nameEs: string;
  icon: string;
  color: string;
  sellIn: number;       // Units or value sold TO channel
  sellOut: number;      // Units or value sold BY channel to consumers
  sellInGrowth?: number;  // YoY growth
  sellOutGrowth?: number;
  share: number;        // % of total market
  subchannels?: {
    id: string;
    name: string;
    nameEs: string;
    sellIn: number;
    sellOut: number;
  }[];
}

interface ChannelMapProps {
  channels: ChannelFlowData[];
  language?: 'en' | 'es';
  /** Total market size for context */
  totalMarketValue?: number;
  /** Currency symbol */
  currency?: string;
  /** Unit label (e.g., "units", "cases", "K") */
  unit?: string;
  className?: string;
}

// Default sample data - would come from API in production
const DEFAULT_CHANNEL_DATA: ChannelFlowData[] = [
  {
    id: 'modern_trade',
    name: 'Modern Trade',
    nameEs: 'Comercio Moderno',
    icon: '🏬',
    color: 'primary',
    sellIn: 4500,
    sellOut: 4200,
    sellInGrowth: 0.05,
    sellOutGrowth: 0.04,
    share: 0.45,
    subchannels: [
      { id: 'hyper', name: 'Hypermarkets', nameEs: 'Hipermercados', sellIn: 1800, sellOut: 1700 },
      { id: 'super', name: 'Supermarkets', nameEs: 'Supermercados', sellIn: 1500, sellOut: 1400 },
      { id: 'warehouse', name: 'Warehouse Clubs', nameEs: 'Clubes de Precio', sellIn: 800, sellOut: 750 },
      { id: 'discount', name: 'Discount Stores', nameEs: 'Tiendas de Descuento', sellIn: 400, sellOut: 350 },
    ],
  },
  {
    id: 'traditional',
    name: 'Traditional Trade',
    nameEs: 'Comercio Tradicional',
    icon: '🏪',
    color: 'warning',
    sellIn: 3800,
    sellOut: 4000,
    sellInGrowth: -0.02,
    sellOutGrowth: 0.01,
    share: 0.38,
    subchannels: [
      { id: 'tiendita', name: 'Corner Stores', nameEs: 'Tienditas', sellIn: 2500, sellOut: 2700 },
      { id: 'mercado', name: 'Public Markets', nameEs: 'Mercados', sellIn: 800, sellOut: 850 },
      { id: 'mayorista', name: 'Wholesalers', nameEs: 'Mayoristas', sellIn: 500, sellOut: 450 },
    ],
  },
  {
    id: 'convenience',
    name: 'Convenience',
    nameEs: 'Conveniencia',
    icon: '⛽',
    color: 'success',
    sellIn: 1000,
    sellOut: 950,
    sellInGrowth: 0.08,
    sellOutGrowth: 0.07,
    share: 0.10,
    subchannels: [
      { id: 'cstore', name: 'C-Stores', nameEs: 'Tiendas 24h', sellIn: 700, sellOut: 680 },
      { id: 'gas', name: 'Gas Stations', nameEs: 'Gasolineras', sellIn: 300, sellOut: 270 },
    ],
  },
  {
    id: 'ecommerce',
    name: 'E-Commerce',
    nameEs: 'E-Commerce',
    icon: '🛒',
    color: 'purple',
    sellIn: 700,
    sellOut: 750,
    sellInGrowth: 0.25,
    sellOutGrowth: 0.30,
    share: 0.07,
    subchannels: [
      { id: 'marketplace', name: 'Marketplaces', nameEs: 'Marketplaces', sellIn: 400, sellOut: 450 },
      { id: 'delivery', name: 'Delivery Apps', nameEs: 'Apps de Entrega', sellIn: 200, sellOut: 220 },
      { id: 'dtc', name: 'Direct to Consumer', nameEs: 'Directo al Consumidor', sellIn: 100, sellOut: 80 },
    ],
  },
];

const colorMap: Record<string, { bg: string; border: string; text: string; light: string }> = {
  primary: { bg: 'bg-primary-500', border: 'border-primary-500', text: 'text-primary-400', light: 'bg-primary-500/20' },
  warning: { bg: 'bg-warning-500', border: 'border-warning-500', text: 'text-warning-400', light: 'bg-warning-500/20' },
  success: { bg: 'bg-success-500', border: 'border-success-500', text: 'text-success-400', light: 'bg-success-500/20' },
  purple: { bg: 'bg-purple-500', border: 'border-purple-500', text: 'text-purple-400', light: 'bg-purple-500/20' },
  danger: { bg: 'bg-danger-500', border: 'border-danger-500', text: 'text-danger-400', light: 'bg-danger-500/20' },
};

export function ChannelMap({
  channels = DEFAULT_CHANNEL_DATA,
  language = 'en',
  totalMarketValue,
  currency = '$',
  unit = 'K',
  className = '',
}: ChannelMapProps) {
  const t = language === 'es';
  const [expandedChannel, setExpandedChannel] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'flow' | 'comparison'>('flow');

  // Calculate totals
  const totals = useMemo(() => {
    const totalSellIn = channels.reduce((sum, ch) => sum + ch.sellIn, 0);
    const totalSellOut = channels.reduce((sum, ch) => sum + ch.sellOut, 0);
    const totalGap = totalSellIn - totalSellOut;
    const gapPercent = totalSellIn > 0 ? ((totalGap / totalSellIn) * 100) : 0;

    return { totalSellIn, totalSellOut, totalGap, gapPercent };
  }, [channels]);

  // Find channels with issues
  const channelsWithHighGap = channels.filter(ch => {
    const gap = ch.sellIn - ch.sellOut;
    const gapPercent = ch.sellIn > 0 ? Math.abs(gap / ch.sellIn) : 0;
    return gapPercent > 0.05; // >5% gap
  });

  const formatNumber = (n: number) => {
    if (n >= 1000) return `${(n / 1000).toFixed(1)}M`;
    return `${n.toFixed(0)}${unit}`;
  };

  const formatGrowth = (g: number | undefined) => {
    if (g === undefined) return null;
    const sign = g >= 0 ? '+' : '';
    return `${sign}${(g * 100).toFixed(0)}%`;
  };

  const getGapStatus = (sellIn: number, sellOut: number) => {
    const gap = sellIn - sellOut;
    const gapPercent = sellIn > 0 ? (gap / sellIn) * 100 : 0;

    if (Math.abs(gapPercent) <= 2) return { status: 'balanced', label: t ? 'Equilibrado' : 'Balanced', color: 'success' };
    if (gapPercent > 2) return { status: 'stocking', label: t ? 'Acumulando' : 'Stocking', color: 'warning' };
    return { status: 'depleting', label: t ? 'Agotándose' : 'Depleting', color: 'danger' };
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header with view toggle */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-white">
            {t ? 'Flujo de Canal: Sell-In vs Sell-Out' : 'Channel Flow: Sell-In vs Sell-Out'}
          </h3>
          <p className="text-sm text-text-secondary">
            {t
              ? 'Visualiza el flujo de producto desde la empresa hasta el consumidor'
              : 'Visualize product flow from company to consumer'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('flow')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              viewMode === 'flow'
                ? 'bg-primary-500/20 text-primary-400 border border-primary-500/40'
                : 'bg-surface-600 text-text-secondary hover:bg-surface-500'
            }`}
          >
            {t ? 'Flujo' : 'Flow'}
          </button>
          <button
            onClick={() => setViewMode('comparison')}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              viewMode === 'comparison'
                ? 'bg-primary-500/20 text-primary-400 border border-primary-500/40'
                : 'bg-surface-600 text-text-secondary hover:bg-surface-500'
            }`}
          >
            {t ? 'Comparar' : 'Compare'}
          </button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card p-3">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
            {t ? 'Total Sell-In' : 'Total Sell-In'}
          </div>
          <div className="text-xl font-bold text-white">
            {currency}{formatNumber(totals.totalSellIn)}
          </div>
          <div className="text-xs text-text-secondary">
            {t ? 'A canales' : 'To channels'}
          </div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
            {t ? 'Total Sell-Out' : 'Total Sell-Out'}
          </div>
          <div className="text-xl font-bold text-white">
            {currency}{formatNumber(totals.totalSellOut)}
          </div>
          <div className="text-xs text-text-secondary">
            {t ? 'A consumidores' : 'To consumers'}
          </div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
            {t ? 'Brecha Neta' : 'Net Gap'}
          </div>
          <div className={`text-xl font-bold ${totals.totalGap >= 0 ? 'text-warning-400' : 'text-danger-400'}`}>
            {totals.totalGap >= 0 ? '+' : ''}{currency}{formatNumber(Math.abs(totals.totalGap))}
          </div>
          <div className="text-xs text-text-secondary">
            {totals.totalGap >= 0 ? (t ? 'Inventario creciendo' : 'Inventory building') : (t ? 'Inventario bajando' : 'Inventory depleting')}
          </div>
        </div>
        <div className="card p-3">
          <div className="text-xs text-text-tertiary uppercase tracking-wider mb-1">
            {t ? 'Canales en Alerta' : 'Channels Alert'}
          </div>
          <div className={`text-xl font-bold ${channelsWithHighGap.length > 0 ? 'text-warning-400' : 'text-success-400'}`}>
            {channelsWithHighGap.length}
          </div>
          <div className="text-xs text-text-secondary">
            {t ? 'Brecha >5%' : 'Gap >5%'}
          </div>
        </div>
      </div>

      {viewMode === 'flow' ? (
        <ChannelFlowView
          channels={channels}
          language={language}
          expandedChannel={expandedChannel}
          setExpandedChannel={setExpandedChannel}
          formatNumber={formatNumber}
          formatGrowth={formatGrowth}
          getGapStatus={getGapStatus}
          currency={currency}
        />
      ) : (
        <ChannelComparisonView
          channels={channels}
          language={language}
          formatNumber={formatNumber}
          formatGrowth={formatGrowth}
          getGapStatus={getGapStatus}
          currency={currency}
        />
      )}

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-text-tertiary pt-2 border-t border-surface-600">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-primary-500 rounded" />
          <span>{t ? 'Sell-In (a canales)' : 'Sell-In (to channels)'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-cyan-500 rounded" />
          <span>{t ? 'Sell-Out (a consumidores)' : 'Sell-Out (to consumers)'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-success-500" />
          <span>{t ? 'Equilibrado' : 'Balanced'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-warning-500" />
          <span>{t ? 'Acumulando inventario' : 'Stocking up'}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-danger-500" />
          <span>{t ? 'Agotándose' : 'Depleting'}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Flow View - Visual diagram showing flow from company → channels → consumers
 */
function ChannelFlowView({
  channels,
  language,
  expandedChannel,
  setExpandedChannel,
  formatNumber,
  formatGrowth,
  getGapStatus,
  currency,
}: {
  channels: ChannelFlowData[];
  language: string;
  expandedChannel: string | null;
  setExpandedChannel: (id: string | null) => void;
  formatNumber: (n: number) => string;
  formatGrowth: (g: number | undefined) => string | null;
  getGapStatus: (sellIn: number, sellOut: number) => { status: string; label: string; color: string };
  currency: string;
}) {
  const t = language === 'es';
  const totalSellIn = channels.reduce((sum, ch) => sum + ch.sellIn, 0);
  const totalSellOut = channels.reduce((sum, ch) => sum + ch.sellOut, 0);

  return (
    <div className="card p-4">
      {/* Flow Diagram Header */}
      <div className="grid grid-cols-3 gap-4 mb-4 text-center">
        <div className="text-sm font-medium text-text-secondary">
          <div className="text-lg mb-1">🏭</div>
          {t ? 'Empresa' : 'Company'}
          <div className="text-xs text-text-tertiary">Sell-In</div>
        </div>
        <div className="text-sm font-medium text-text-secondary">
          <div className="text-lg mb-1">🏪</div>
          {t ? 'Canales' : 'Channels'}
          <div className="text-xs text-text-tertiary">{t ? 'Distribución' : 'Distribution'}</div>
        </div>
        <div className="text-sm font-medium text-text-secondary">
          <div className="text-lg mb-1">👥</div>
          {t ? 'Consumidor' : 'Consumer'}
          <div className="text-xs text-text-tertiary">Sell-Out</div>
        </div>
      </div>

      {/* Channel Flow Rows */}
      <div className="space-y-3">
        {channels.map((channel) => {
          const gapInfo = getGapStatus(channel.sellIn, channel.sellOut);
          const isExpanded = expandedChannel === channel.id;
          const sellInWidth = totalSellIn > 0 ? (channel.sellIn / totalSellIn) * 100 : 0;
          const sellOutWidth = totalSellOut > 0 ? (channel.sellOut / totalSellOut) * 100 : 0;

          return (
            <div key={channel.id}>
              <button
                onClick={() => setExpandedChannel(isExpanded ? null : channel.id)}
                className="w-full text-left"
              >
                <div className="grid grid-cols-3 gap-4 items-center p-3 rounded-lg bg-surface-700/50 hover:bg-surface-600/50 transition-colors">
                  {/* Sell-In Bar */}
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-white">{currency}{formatNumber(channel.sellIn)}</span>
                      {formatGrowth(channel.sellInGrowth) && (
                        <span className={`text-xs ${(channel.sellInGrowth || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                          {formatGrowth(channel.sellInGrowth)}
                        </span>
                      )}
                    </div>
                    <div className="h-4 bg-surface-600 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary-500 rounded-full transition-all duration-300"
                        style={{ width: `${Math.max(sellInWidth, 5)}%` }}
                      />
                    </div>
                  </div>

                  {/* Channel Info (center) */}
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-xl">{channel.icon}</span>
                    <div className="text-center">
                      <div className="font-medium text-white text-sm">
                        {t ? channel.nameEs : channel.name}
                      </div>
                      <div className="flex items-center justify-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${colorMap[gapInfo.color]?.bg || 'bg-gray-500'}`} />
                        <span className="text-xs text-text-tertiary">{gapInfo.label}</span>
                      </div>
                    </div>
                    <svg
                      className={`w-4 h-4 text-text-tertiary transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {/* Sell-Out Bar */}
                  <div className="relative">
                    <div className="flex items-center gap-2 mb-1 justify-end">
                      {formatGrowth(channel.sellOutGrowth) && (
                        <span className={`text-xs ${(channel.sellOutGrowth || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                          {formatGrowth(channel.sellOutGrowth)}
                        </span>
                      )}
                      <span className="text-sm font-medium text-white">{currency}{formatNumber(channel.sellOut)}</span>
                    </div>
                    <div className="h-4 bg-surface-600 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-cyan-500 rounded-full transition-all duration-300 ml-auto"
                        style={{ width: `${Math.max(sellOutWidth, 5)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </button>

              {/* Expanded Subchannel View */}
              {isExpanded && channel.subchannels && (
                <div className="mt-2 ml-8 mr-8 space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
                  {channel.subchannels.map((sub) => {
                    const subGapInfo = getGapStatus(sub.sellIn, sub.sellOut);
                    const subSellInWidth = channel.sellIn > 0 ? (sub.sellIn / channel.sellIn) * 100 : 0;
                    const subSellOutWidth = channel.sellOut > 0 ? (sub.sellOut / channel.sellOut) * 100 : 0;

                    return (
                      <div key={sub.id} className="grid grid-cols-3 gap-4 items-center p-2 rounded bg-surface-800/50">
                        <div>
                          <div className="text-xs text-text-secondary mb-1">{currency}{formatNumber(sub.sellIn)}</div>
                          <div className="h-2 bg-surface-600 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-primary-400/70 rounded-full"
                              style={{ width: `${Math.max(subSellInWidth, 3)}%` }}
                            />
                          </div>
                        </div>
                        <div className="flex items-center justify-center gap-2">
                          <span className="text-xs text-text-secondary">{t ? sub.nameEs : sub.name}</span>
                          <span className={`w-1.5 h-1.5 rounded-full ${colorMap[subGapInfo.color]?.bg || 'bg-gray-500'}`} />
                        </div>
                        <div>
                          <div className="text-xs text-text-secondary mb-1 text-right">{currency}{formatNumber(sub.sellOut)}</div>
                          <div className="h-2 bg-surface-600 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-cyan-400/70 rounded-full ml-auto"
                              style={{ width: `${Math.max(subSellOutWidth, 3)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Comparison View - Side-by-side bar chart comparing sell-in vs sell-out
 */
function ChannelComparisonView({
  channels,
  language,
  formatNumber,
  formatGrowth,
  getGapStatus,
  currency,
}: {
  channels: ChannelFlowData[];
  language: string;
  formatNumber: (n: number) => string;
  formatGrowth: (g: number | undefined) => string | null;
  getGapStatus: (sellIn: number, sellOut: number) => { status: string; label: string; color: string };
  currency: string;
}) {
  const t = language === 'es';
  const maxValue = Math.max(...channels.flatMap(ch => [ch.sellIn, ch.sellOut]));

  return (
    <div className="card p-4">
      <div className="space-y-4">
        {channels.map((channel) => {
          const gapInfo = getGapStatus(channel.sellIn, channel.sellOut);
          const gap = channel.sellIn - channel.sellOut;
          const gapPercent = channel.sellIn > 0 ? ((gap / channel.sellIn) * 100) : 0;
          const sellInWidth = maxValue > 0 ? (channel.sellIn / maxValue) * 100 : 0;
          const sellOutWidth = maxValue > 0 ? (channel.sellOut / maxValue) * 100 : 0;

          return (
            <div key={channel.id} className="border-b border-surface-600 pb-4 last:border-0 last:pb-0">
              {/* Channel Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl">{channel.icon}</span>
                  <span className="font-medium text-white">{t ? channel.nameEs : channel.name}</span>
                  <span className="text-xs text-text-tertiary">({(channel.share * 100).toFixed(0)}%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${colorMap[gapInfo.color]?.bg || 'bg-gray-500'}`} />
                  <span className={`text-sm font-medium ${colorMap[gapInfo.color]?.text || 'text-gray-400'}`}>
                    {gapInfo.label}
                  </span>
                  <span className="text-xs text-text-tertiary">
                    ({gapPercent >= 0 ? '+' : ''}{gapPercent.toFixed(1)}%)
                  </span>
                </div>
              </div>

              {/* Comparison Bars */}
              <div className="space-y-2">
                {/* Sell-In */}
                <div className="flex items-center gap-3">
                  <div className="w-20 text-xs text-text-tertiary text-right">Sell-In</div>
                  <div className="flex-1 h-5 bg-surface-600 rounded overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded flex items-center justify-end pr-2 transition-all duration-300"
                      style={{ width: `${Math.max(sellInWidth, 10)}%` }}
                    >
                      <span className="text-xs text-white font-medium">{currency}{formatNumber(channel.sellIn)}</span>
                    </div>
                  </div>
                  {formatGrowth(channel.sellInGrowth) && (
                    <div className={`w-12 text-xs ${(channel.sellInGrowth || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                      {formatGrowth(channel.sellInGrowth)}
                    </div>
                  )}
                </div>

                {/* Sell-Out */}
                <div className="flex items-center gap-3">
                  <div className="w-20 text-xs text-text-tertiary text-right">Sell-Out</div>
                  <div className="flex-1 h-5 bg-surface-600 rounded overflow-hidden">
                    <div
                      className="h-full bg-cyan-500 rounded flex items-center justify-end pr-2 transition-all duration-300"
                      style={{ width: `${Math.max(sellOutWidth, 10)}%` }}
                    >
                      <span className="text-xs text-white font-medium">{currency}{formatNumber(channel.sellOut)}</span>
                    </div>
                  </div>
                  {formatGrowth(channel.sellOutGrowth) && (
                    <div className={`w-12 text-xs ${(channel.sellOutGrowth || 0) >= 0 ? 'text-success-400' : 'text-danger-400'}`}>
                      {formatGrowth(channel.sellOutGrowth)}
                    </div>
                  )}
                </div>
              </div>

              {/* Gap Indicator */}
              <div className="mt-2 flex items-center gap-2 text-xs">
                <span className="text-text-tertiary">{t ? 'Brecha' : 'Gap'}:</span>
                <span className={`font-medium ${gap >= 0 ? 'text-warning-400' : 'text-danger-400'}`}>
                  {gap >= 0 ? '+' : ''}{currency}{formatNumber(Math.abs(gap))}
                </span>
                <span className="text-text-tertiary">
                  ({gap >= 0 ? (t ? 'inventario creciendo' : 'inventory building') : (t ? 'inventario bajando' : 'inventory depleting')})
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Compact version for embedding in other views
 */
export function ChannelMapCompact({
  channels = DEFAULT_CHANNEL_DATA,
  language = 'en',
  className = '',
}: {
  channels?: ChannelFlowData[];
  language?: 'en' | 'es';
  className?: string;
}) {
  const t = language === 'es';

  return (
    <div className={`space-y-2 ${className}`}>
      {channels.slice(0, 4).map((channel) => {
        const gap = channel.sellIn - channel.sellOut;
        const gapPercent = channel.sellIn > 0 ? ((gap / channel.sellIn) * 100) : 0;
        const isPositive = gap >= 0;

        return (
          <div key={channel.id} className="flex items-center gap-3 p-2 rounded bg-surface-700/50">
            <span className="text-lg">{channel.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">
                {t ? channel.nameEs : channel.name}
              </div>
              <div className="text-xs text-text-tertiary">
                {(channel.share * 100).toFixed(0)}% {t ? 'del mercado' : 'of market'}
              </div>
            </div>
            <div className="text-right">
              <div className={`text-sm font-medium ${isPositive ? 'text-warning-400' : 'text-danger-400'}`}>
                {isPositive ? '+' : ''}{gapPercent.toFixed(1)}%
              </div>
              <div className="text-xs text-text-tertiary">{t ? 'brecha' : 'gap'}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ChannelMap;
